import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import GiftingCategoryProductCardBrandRefresh from './GiftingCategoryProductCardBrandRefresh';
import {
  createGiftingProductRowDragScroll,
  handleGiftingCategoryCarouselWheel,
} from './giftingCategoryProductRowDragScroll';
import rowStyles from './GiftingCategoryProductRowBrandRefresh.module.css';

// Keep in sync with .scrollIndicator / .scrollIndicatorThumb widths in module CSS
const SCROLL_INDICATOR_WIDTH_DESKTOP = 400;
const SCROLL_INDICATOR_WIDTH_MOBILE = 240;
const SCROLL_THUMB_WIDTH_DESKTOP = 96;
const SCROLL_THUMB_WIDTH_MOBILE = 67;

function GiftingCategoryProductRowBrandRefresh({
  products,
  loading,
  menuError,
  activeCategoryId,
}) {
  const trackRef = useRef(null);
  const indicatorRef = useRef(null);
  const dragStateRef = useRef({
    isActive: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    startScrollLeft: 0,
    didDrag: false,
    axisLocked: false,
  });
  const [thumbOffset, setThumbOffset] = useState(0);

  // Mouse drag-to-scroll on track; touch uses native overflow-x on .productTrack
  const dragScrollHandlers = useMemo(
    () =>
      createGiftingProductRowDragScroll({
        getTrackElement: () => trackRef.current,
        dragStateRef,
        draggingClassName: rowStyles.productTrackDragging,
      }),
    []
  );

  const updateScrollThumb = useCallback(() => {
    const el = trackRef.current;
    const indicatorEl = indicatorRef.current;
    if (!el) return;

    const isMobileViewport =
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 767px)').matches;
    const fallbackIndicatorWidth = isMobileViewport
      ? SCROLL_INDICATOR_WIDTH_MOBILE
      : SCROLL_INDICATOR_WIDTH_DESKTOP;
    const thumbWidth = isMobileViewport
      ? SCROLL_THUMB_WIDTH_MOBILE
      : SCROLL_THUMB_WIDTH_DESKTOP;
    const indicatorWidth =
      indicatorEl?.clientWidth || fallbackIndicatorWidth;

    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 1) {
      setThumbOffset(0);
      return;
    }

    const ratio = el.scrollLeft / maxScroll;
    const maxThumbTravel = Math.max(0, indicatorWidth - thumbWidth);
    setThumbOffset(ratio * maxThumbTravel);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;

    const onWheel = (e) => {
      handleGiftingCategoryCarouselWheel(e, el);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    updateScrollThumb();
    el.addEventListener('scroll', updateScrollThumb, { passive: true });
    window.addEventListener('resize', updateScrollThumb);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateScrollThumb);
      resizeObserver.observe(el);
      if (indicatorRef.current) {
        resizeObserver.observe(indicatorRef.current);
      }
    }

    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('scroll', updateScrollThumb);
      window.removeEventListener('resize', updateScrollThumb);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [products.length, updateScrollThumb]);

  // New category tab: show products from the start of the row
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    el.scrollLeft = 0;
    Object.assign(dragStateRef.current, {
      isActive: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      startScrollLeft: 0,
      didDrag: false,
      axisLocked: false,
    });
    updateScrollThumb();
  }, [activeCategoryId, updateScrollThumb]);

  if (loading && (!products || products.length === 0)) {
    return (
      <div
        className={rowStyles.loadingRow}
        data-part="category-product-loading"
      >
        Loading products…
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div
        className={rowStyles.emptyRow}
        data-part="category-product-empty"
      >
        {menuError
          ? 'Unable to load the menu. Please refresh or try again.'
          : 'No products available in this category right now.'}
      </div>
    );
  }

  return (
    <div className={rowStyles.productSection} data-part="category-product-section">
      <div
        ref={trackRef}
        className={`no-scrollbar ${rowStyles.productTrack}`}
        role="region"
        aria-label="Gifting products"
        data-part="category-product-track"
        onPointerDownCapture={dragScrollHandlers.onPointerDownCapture}
        onPointerMove={dragScrollHandlers.onPointerMove}
        onPointerUp={dragScrollHandlers.onPointerUp}
        onPointerCancel={dragScrollHandlers.onPointerCancel}
        onClickCapture={dragScrollHandlers.onClickCapture}
        onDragStartCapture={dragScrollHandlers.onDragStartCapture}
      >
        {products.map((product) => (
          <GiftingCategoryProductCardBrandRefresh
            key={product.id}
            product={product}
          />
        ))}
      </div>
      <div
        ref={indicatorRef}
        className={rowStyles.scrollIndicator}
        aria-hidden="true"
        data-part="category-product-scroll-indicator"
      >
        <div
          className={rowStyles.scrollIndicatorThumb}
          style={{ transform: `translateX(${thumbOffset}px)` }}
        />
      </div>
    </div>
  );
}

GiftingCategoryProductRowBrandRefresh.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      productSlug: PropTypes.string,
      productImage: PropTypes.string,
      productImageSmall: PropTypes.string,
      metaTags: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
        })
      ),
    })
  ),
  loading: PropTypes.bool,
  menuError: PropTypes.bool,
  activeCategoryId: PropTypes.string,
};

GiftingCategoryProductRowBrandRefresh.defaultProps = {
  products: [],
  loading: false,
  menuError: false,
  activeCategoryId: undefined,
};

export default GiftingCategoryProductRowBrandRefresh;
